/**
 * API 客户端
 * 处理与 Cloudflare Workers API 的通信
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface Node {
  id: string;
  date: string;
  title: string;
  description: string;
  icon: 'user' | 'heart' | 'love' | 'star' | 'landmark' | 'calendar';
  perspective: 'male' | 'female';
  stage?: string;
  heart_rate?: number;
  attachments?: Array<{
    id: string;
    node_id: string;
    type: 'image' | 'video' | 'audio';
    url: string;
    caption?: string;
  }>;
}

export interface CreateNodeDto {
  date: string;
  title: string;
  description: string;
  icon: 'user' | 'heart' | 'love' | 'star' | 'landmark' | 'calendar';
  perspective: 'male' | 'female';
  stage?: string;
  heart_rate?: number;
}

export interface UpdateNodeDto {
  date?: string;
  title?: string;
  description?: string;
  icon?: 'user' | 'heart' | 'love' | 'star' | 'landmark' | 'calendar';
  perspective?: 'male' | 'female';
  stage?: string;
  heart_rate?: number;
}

/**
 * API 客户端类
 */
export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 通用请求方法
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * GET /api/nodes - 获取所有节点
   */
  async getAllNodes(): Promise<ApiResponse<Node[]>> {
    return this.request<Node[]>('/nodes');
  }

  /**
   * GET /api/nodes/:id - 获取单个节点
   */
  async getNodeById(id: string): Promise<ApiResponse<Node>> {
    return this.request<Node>(`/nodes/${id}`);
  }

  /**
   * POST /api/nodes - 创建新节点
   */
  async createNode(dto: CreateNodeDto): Promise<ApiResponse<Node>> {
    return this.request<Node>('/nodes', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  /**
   * PUT /api/nodes/:id - 更新节点
   */
  async updateNode(id: string, dto: UpdateNodeDto): Promise<ApiResponse<Node>> {
    return this.request<Node>(`/nodes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    });
  }

  /**
   * DELETE /api/nodes/:id - 删除节点
   */
  async deleteNode(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/nodes/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * POST /api/upload - 上传附件
   */
  async uploadAttachment(
    nodeId: string,
    file: File,
    caption?: string
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('node_id', nodeId);
    if (caption) {
      formData.append('caption', caption);
    }

    try {
      const response = await fetch(`${this.baseUrl}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Upload failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * DELETE /api/attachments/:id - 删除附件
   */
  async deleteAttachment(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/attachments/${id}`, {
      method: 'DELETE',
    });
  }
}

// 导出单例实例
export const apiClient = new ApiClient();